import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './DriverScoreCategoryPage.css';

// Import the same data cache approach
import { clearDriverSegmentationCache } from '../../utils/dataCache';
import tooltipTexts from '../../utils/tooltipTexts';

const categories = ['All', 'Excellent', 'Good', 'Average', 'Poor'];

const DriverScoreCategoryPage = ({ vehicleId, embedded = false }) => {
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [remarks, setRemarks] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [bridgeWidth, setBridgeWidth] = useState(0);
  const [bridgeLeft, setBridgeLeft] = useState(0);
  const navigate = useNavigate();

  const fetchDriverData = async (forceRefresh = false) => {
    setLoading(true);
    setError('');
    
    try {
      // Get admin location from localStorage user object
      const user = JSON.parse(localStorage.getItem('user'));
      const locationId = user?.locationId;
      
      if (!locationId) {
        setError('Admin location not found.');
        setDrivers([]);
        setLoading(false);
        return;
      }
      
      // Build the URL with locationId and vehicleId if provided
      let url = `http://eframeehs.in:3001/api/vehiclewiseleaderboard?locationId=${locationId}`;
      
      if (vehicleId) {
        url += `&vehicleId=${vehicleId}`;
      }
      
      // Add cache busting if forcing refresh
      if (forceRefresh) {
        url += `&_t=${Date.now()}`;
      }
      
      const response = await fetch(url, {
        headers: forceRefresh ? {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        } : {}
      });
      
      if (!response.ok) throw new Error('Failed to fetch driver data');
      const data = await response.json();
      
      setDrivers(data);
      
      // If we successfully updated data, also clear the segmentation cache
      if (forceRefresh) {
        clearDriverSegmentationCache();
      }
    } catch (err) {
      setError('Failed to fetch driver data.');
      setDrivers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDriverData();
    
    // Set up category underline position
    const categoryIndex = categories.indexOf(selectedCategory);
    if (categoryIndex >= 0) {
      const categoryWidth = 100 / categories.length;
      setBridgeWidth(categoryWidth);
      setBridgeLeft(categoryIndex * categoryWidth);
    }
  }, [selectedCategory, vehicleId]);

  // Update the bridge position when category changes
  useEffect(() => {
    const categoryIndex = categories.indexOf(selectedCategory);
    if (categoryIndex >= 0) {
      const categoryWidth = 100 / categories.length;
      setBridgeWidth(categoryWidth);
      setBridgeLeft(categoryIndex * categoryWidth);
    }
  }, [selectedCategory]);

  // Filter drivers by selected category
  const filteredDrivers = selectedCategory === 'All'
    ? drivers
    : drivers.filter(driver => driver.DriverScoreCategory === selectedCategory);

  const handleAddRemarks = (driver) => {
    setSelectedDriver(driver);
    setRemarks('');
    setIsModalOpen(true);
  };

  const handleSubmitRemarks = async () => {
    if (!selectedDriver) return;
    setErrorMessage('');
    try {
      const response = await fetch('http://eframeehs.in:3001/api/update-vehicle-remarks', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userPK: selectedDriver.UserPK,
          vehicleID: selectedDriver.VehicleID,
          remarks: remarks.trim(),
        }),
      });
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to update remarks');
      }
      setIsModalOpen(false);
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 2000);
      
      // Refresh the data to show updated remarks
      fetchDriverData(true);
    } catch (err) {
      setErrorMessage(err.message || 'Failed to update remarks');
    }
  };

  const handleBackToSegmentation = () => {
    navigate('/driver-segmentation');
  };

  return (
    <div className={`driver-score-category-page ${embedded ? 'embedded' : ''}`}>
      {showSuccess && (
        <div className="success-notification">Remarks successfully submitted!</div>
      )}
      <div className="page-header">
        <div className="header-left">
          {!embedded && (
            <button 
              className="back-button" 
              onClick={handleBackToSegmentation}
              title="Back to Segmentation"
            >
              ← Back
            </button>
          )}
          <h1>
            Driver Score Category
            <span className="header-tooltip">
              <span className="info-icon">i</span>
              <span className="tooltip-text">{tooltipTexts.driverScoreCategoryHeader}</span>
            </span>
          </h1>
        </div>
      </div>
      <div className="category-bars">
        {categories.map((category) => (
          <div
            key={category}
            className={`category-bar ${category.toLowerCase()}${selectedCategory === category ? ' selected' : ''}`}
            onClick={() => setSelectedCategory(category)}
            style={{ cursor: 'pointer' }}
          >
            <div>{category}</div>
          </div>
        ))}
        <div
          className="category-underline"
          style={{
            width: `${bridgeWidth}%`,
            left: `${bridgeLeft}%`,
          }}
        ></div>
      </div>
      <div className="category-divider"></div>
      <div className="drivers-table-container">
        {loading ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <div>Loading driver data...</div>
          </div>
        ) : error ? (
          <div className="no-data">{error}</div>
        ) : filteredDrivers.length === 0 ? (
          <div className="no-data">No drivers found for this category.</div>
        ) : (
          <table className="drivers-table">
            <thead>
              <tr>
                <th>SI No.</th>
                <th>Username</th>
                <th>Mobile Equipment Type</th>
                <th>Driver Score Category</th>
                <th>Score</th>
                <th>Remarks</th>
              </tr>
            </thead>
            <tbody>
              {filteredDrivers.map((driver, idx) => (
                <tr key={driver.UserPK} className={idx % 2 === 1 ? 'even-row' : ''}>
                  <td>{idx + 1}</td>
                  <td>{driver.EmployeeName}</td>
                  <td>{driver.ScreenName}</td>
                  <td>
                    <span className={`category-badge ${selectedCategory !== 'All' ? selectedCategory.toLowerCase() : (driver.DriverScoreCategory?.toLowerCase() || '')}`}>
                      {driver.DriverScoreCategory}
                    </span>
                  </td>
                  <td className="score-cell">{driver.AverageScore}</td>
                  <td>
                    <button type="button" className="add-remarks-btn" onClick={() => handleAddRemarks(driver)}>
                      Add Remarks
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {/* Modal for remarks */}
      {isModalOpen && (
        <div className="modal-overlay" onClick={() => setIsModalOpen(false)}>
          <div className="remarks-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Add Remarks for {selectedDriver?.EmployeeName}</h3>
              <button className="close-button" onClick={() => setIsModalOpen(false)}>&times;</button>
            </div>
            {errorMessage && <div className="error-message">{errorMessage}</div>}
            <textarea
              className="remarks-textarea"
              value={remarks}
              onChange={e => setRemarks(e.target.value)}
              placeholder="Enter your remarks here..."
            />
            <div className="modal-buttons">
              <button className="modal-button cancel" onClick={() => setIsModalOpen(false)}>Cancel</button>
              <button className="modal-button submit" onClick={handleSubmitRemarks} disabled={!remarks.trim()}>
                Submit
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DriverScoreCategoryPage; 